import { Injectable } from '@angular/core';
import { FlowConfig } from '../../components/stepper/flow-config.interface';
import { AddUserTriagePageMvpComponent } from '../../components/stepper-steps/grant-access-flow-mvp/add-user-triage-page-mvp/add-user-triage-page-mvp.component';
import { ContentUtil } from '../../utils/content/content.util';
import { AdobeMethods } from '../adobe-launch/adobe-launch.methods';
import { AdobeAnalyticsProcessStep } from '../adobe-launch/adobe-launch.process';
import { AnalyticsCtaType, CtaNames } from '../adobe-launch/adobe-launch.cta';
import { GafAccessLevelMvpComponent } from '../../components/stepper-steps/grant-access-flow-mvp/gaf-access-level-mvp/gaf-access-level-mvp.component';
import { GafSelectUserStepMvpComponent } from '../../components/stepper-steps/grant-access-flow-mvp/gaf-select-user-step-mvp/gaf-select-user-step-mvp.component';
import { GafMvpAccessAgreementComponent } from '../../components/stepper-steps/grant-access-flow-mvp/gaf-mvp-access-agreement/gaf-mvp-access-agreement.component';
import { GafReviewAndSubmitStepMvpComponent } from '../../components/stepper-steps/grant-access-flow-mvp/gaf-review-and-submit-step-mvp/gaf-review-and-submit-step-mvp.component';
import { GafAccountsComponent } from '../../components/stepper-steps/grant-access-flow-mvp/gaf-accounts/gaf-accounts.component';
import { FakeStepOneComponent } from '../../components/stepper/test-components/fake-step-one/fake-step-one.component';
import { FakeStepTwoComponent } from '../../components/stepper/test-components/fake-step-two/fake-step-two.component';
import {
    ExistingGranteeRegulatoryQuestionsComponent,
    NewGranteeRegulatoryQuestionsComponent,
    NonVgClientGranteeRegulatoryQuestionsComponent,
} from '../../components/stepper-steps/grantee-flow/grantee-regulatory-questions/grantee-regulatory-questions.component';
import {
    ExistingGranteeAuthorizationAgreementComponent,
    NewGranteeAuthorizationAgreementComponent,
    NonVgClientGranteeAuthorizationAgreementComponent,
} from '../../components/stepper-steps/grantee-flow/grantee-authorization-agreement/grantee-authorization-agreement.component';
import {
    ExistingGranteeSuccessPageComponent,
    NewGranteeInfoOnlySuccessPageComponent,
    NewGranteeLimitedSuccessPageComponent,
    NvcInfoOnlySuccessPageComponent,
    NvcLimitedSuccessPageComponent,
} from '../../components/stepper-steps/grantee-flow/grantee-success-page/grantee-success-page.component';
import { GranteePinInputComponent } from '../../components/stepper-steps/grantee-flow/grantee-pin-input/grantee-pin-input.component';
import { FlowKey } from '../../enums/flow-keys.enum';
import { RafSelectUserStepComponent } from '../../components/stepper-steps/remove-access-flow/raf-select-user-step/raf-select-user-step.component';
import { RafReviewStepComponent } from '../../components/stepper-steps/remove-access-flow/raf-review-step/raf-review-step.component';
import { isInternalEnvironment } from 'src/common/utils/common.util';
import { RafSelectAccountsStepComponent } from '../../components/stepper-steps/remove-access-flow/raf-select-accounts-step/raf-select-accounts-step.component';
@Injectable({
    providedIn: 'root',
})
export class FlowConfigService {
    private isPrimaryButtonDisabledForCrew = (value: boolean) => isInternalEnvironment() && value;

    getFlowConfig(flowKey: FlowKey): FlowConfig {
        switch (flowKey) {
            case FlowKey.GAF:
                return this.gaf();
            case FlowKey.RAF:
                return this.raf();
            case FlowKey.EXISTING_GRANTEE_LIMITED:
                return this.existingGranteeLimited();
            case FlowKey.NEW_GRANTEE_INFO_ONLY:
                return this.newGranteeInfoOnly();
            case FlowKey.NEW_GRANTEE_LIMITED:
                return this.newGranteeLimited();
            case FlowKey.NON_VG_CLIENT_INFO_ONLY:
                return this.nonVgClientInfoOnly();
            case FlowKey.NON_VG_CLIENT_LIMITED:
                return this.nonVgClientLimited();
            case FlowKey.TEST:
                return this.test();
        }
    }

    gaf(): FlowConfig {
        return {
            flowCaption: ContentUtil.grantorFragmentContent.add_user.lblGiveAccess,
            buttons: {
                contactUs: true,
                quit: true,
            },
            steps: [
                {
                    component: AddUserTriagePageMvpComponent,
                    inputs: {},
                    primaryButton: '',
                    secondaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navBack,

                    title: ContentUtil.grantorFragmentContent.add_user.lblAddUserSteps
                        .lblGranteeType,
                    adobeLocation: 'triage',
                    customAdobeTags: {
                        onQuit: [
                            {
                                method: AdobeMethods.TRACK_ACTION,
                                data: [
                                    {
                                        location: AdobeAnalyticsProcessStep.GAF_TRIAGE,
                                        ctaName: CtaNames.QUIT,
                                        ctaType: AnalyticsCtaType.BUTTON,
                                    },
                                ],
                            },
                        ],
                    },
                    isStandaloneStep: false,
                    isStepIndicatorNeeded: true,
                },
                {
                    component: GafAccessLevelMvpComponent,
                    inputs: {},
                    primaryButton: '',
                    secondaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navBack,
                    title: ContentUtil.grantorFragmentContent.add_user.lblAddUserSteps
                        .lblAccessLevel,
                    adobeLocation: AdobeAnalyticsProcessStep.GAF_SELECT_ACCESS_LEVEL,
                    isStandaloneStep: false,
                    isStepIndicatorNeeded: true,
                },
                {
                    component: GafSelectUserStepMvpComponent,
                    inputs: {},
                    primaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navContinue,
                    secondaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navBack,
                    title: ContentUtil.grantorFragmentContent.add_user.lblAddUserSteps.lblPerson,
                    adobeLocation: AdobeAnalyticsProcessStep.GAF_SELECT_PERSON,
                    isStandaloneStep: false,
                    isStepIndicatorNeeded: true,
                },
                {
                    component: GafAccountsComponent,
                    inputs: {},
                    primaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navContinue,
                    secondaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navBack,
                    title: 'Choose accounts',
                    adobeLocation: AdobeAnalyticsProcessStep.GAF_CHOOSE_ACCOUNTS,
                    isStandaloneStep: false,
                    isStepIndicatorNeeded: true,
                },
                {
                    component: GafMvpAccessAgreementComponent,
                    inputs: {},
                    primaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navAgreeContinue,
                    secondaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navBack,
                    title: ContentUtil.grantorFragmentContent.add_user.lblAddUserSteps.lblAgreement,
                    adobeLocation: AdobeAnalyticsProcessStep.GAF_ACCESS_AGREEMENT,
                    isStandaloneStep: false,
                    isStepIndicatorNeeded: true,
                },
                {
                    component: GafReviewAndSubmitStepMvpComponent,
                    inputs: {},
                    primaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navSubmit,
                    secondaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navBack,
                    title: ContentUtil.grantorFragmentContent.add_user.lblAddUserSteps
                        .lblReviewSubmitStep,
                    adobeLocation: AdobeAnalyticsProcessStep.GAF_REVIEW_AND_SUBMIT,
                    isStandaloneStep: false,
                    isStepIndicatorNeeded: true,
                    isPrimaryButtonDisabledForCrew: this.isPrimaryButtonDisabledForCrew(true),
                },
            ],
        };
    }

    raf(): FlowConfig {
        return {
            flowCaption: 'Remove access',
            buttons: {
                contactUs: false,
                quit: true,
            },
            steps: [
                {
                    component: RafSelectUserStepComponent,
                    inputs: {},
                    primaryButton: ContentUtil.grantorFragmentContent.remove_user.frmcRemoveAccessSelectUser.navNext,
                    secondaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navBack,
                    title: ContentUtil.grantorFragmentContent.add_user.lblAddUserSteps.lblPerson,
                    adobeLocation: AdobeAnalyticsProcessStep.RAF_SELECT_USER_TO_REMOVE,
                    isStandaloneStep: false,
                    isStepIndicatorNeeded: true,
                    isPrimaryButtonDisabledForCrew: this.isPrimaryButtonDisabledForCrew(true),
                },
                {
                    component: RafSelectAccountsStepComponent,
                    inputs: {},
                    primaryButton: ContentUtil.grantorFragmentContent.remove_user.frmcRemoveAccessSelectUser.navNext,
                    secondaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navBack,
                    title: 'Accounts',
                    adobeLocation: AdobeAnalyticsProcessStep.RAF_SELECT_ACCOUNTS_TO_REMOVE,
                    isStandaloneStep: false,
                    isStepIndicatorNeeded: true,
                    isPrimaryButtonDisabledForCrew: this.isPrimaryButtonDisabledForCrew(true),
                },
                {
                    component: RafReviewStepComponent,
                    inputs: {},
                    primaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navSubmit,
                    isPrimaryButtonDisabledForCrew: this.isPrimaryButtonDisabledForCrew(true),
                    secondaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navBack,
                    title: ContentUtil.grantorFragmentContent.add_user.lblAddUserSteps.lblReviewSubmitStep,
                    adobeLocation: AdobeAnalyticsProcessStep.RAF_REVIEW_AND_SUBMIT,
                    isStandaloneStep: false,
                    isStepIndicatorNeeded: true,
                }
            ],
        };
    }

    test(): FlowConfig {
        return {
            flowCaption: 'test',
            steps: [
                {
                    isStepIndicatorNeeded: true,
                    isStandaloneStep: false,
                    component: FakeStepOneComponent,
                    inputs: {},
                    primaryButton: 'next',
                    secondaryButton: '',
                    title: 'Fake component one',
                    adobeLocation: 'step1',
                },
                {
                    isStepIndicatorNeeded: true,
                    isStandaloneStep: false,
                    component: FakeStepTwoComponent,
                    inputs: {},
                    primaryButton: 'submit',
                    secondaryButton: 'back',
                    title: 'Fake component two',
                    adobeLocation: null,
                },
            ],
        };
    }

    existingGranteeLimited(): FlowConfig {
        return {
            flowCaption: ContentUtil.granteeFragmentsContent.acceptLimitedAuthorityLabel,
            steps: [
                {
                    isStandaloneStep: false,
                    isStepIndicatorNeeded: false,
                    component: ExistingGranteeRegulatoryQuestionsComponent,
                    inputs: {},
                    primaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navContinue,
                    secondaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navBack,
                    title: '',
                    adobeLocation: AdobeAnalyticsProcessStep.GRANTEE_REG_QUESTIONS_GRANTEE_ON_LIST,
                    customAdobeTags: {
                        onQuit: [
                            {
                                method: AdobeMethods.TRACK_ACTION,
                                data: [
                                    {
                                        location:
                                            AdobeAnalyticsProcessStep.GRANTEE_REG_QUESTIONS_GRANTEE_ON_LIST,
                                        ctaName: CtaNames.QUIT,
                                        ctaType: AnalyticsCtaType.LINK,
                                        fireTrackLink: true,
                                    },
                                ],
                            },
                        ],
                    },
                },
                {
                    isStepIndicatorNeeded: false,
                    isStandaloneStep: false,
                    component: ExistingGranteeAuthorizationAgreementComponent,
                    inputs: {},
                    primaryButton:
                        ContentUtil.granteeFragmentsContent.trmAuthorizationAgreement
                            .navAgreeSubmit,
                    secondaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navBack,
                    title: '',
                    adobeLocation: AdobeAnalyticsProcessStep.GRANTEE_AUTH_AGREEMENT_ON_LIST,
                    customAdobeTags: {
                        onQuit: [
                            {
                                method: AdobeMethods.TRACK_ACTION,
                                data: [
                                    {
                                        location:
                                            AdobeAnalyticsProcessStep.GRANTEE_AUTH_AGREEMENT_ON_LIST,
                                        ctaName: CtaNames.QUIT,
                                        ctaType: AnalyticsCtaType.LINK,
                                        fireTrackLink: true,
                                    },
                                ],
                            },
                        ],
                    },
                },
                {
                    isStepIndicatorNeeded: false,
                    isStandaloneStep: true,
                    component: ExistingGranteeSuccessPageComponent,
                    inputs: {},
                    primaryButton: '', // TODO: Replace this hardcoded value with AEM content fragment when it's available
                    secondaryButton: '',
                    title: '',
                    adobeLocation: AdobeAnalyticsProcessStep.GRANTEE_SUCCESS_ON_LIST,
                    customAdobeTags: {
                        onQuit: [
                            {
                                method: AdobeMethods.TRACK_ACTION,
                                data: [
                                    {
                                        location: AdobeAnalyticsProcessStep.GRANTEE_SUCCESS_ON_LIST,
                                        ctaName: CtaNames.QUIT,
                                        ctaType: AnalyticsCtaType.LINK,
                                        fireTrackLink: true,
                                    },
                                ],
                            },
                        ],
                    },
                },
            ],
            buttons: {
                contactUs: false,
                quit: true,
            },
        };
    }

    newGranteeInfoOnly(): FlowConfig {
        return this.createInfoOnlyFlowConfig(
            AdobeAnalyticsProcessStep.GRANTEE_NEW_PIN_INFO_ONLY,
            AdobeAnalyticsProcessStep.GRANTEE_INFO_ONLY_NOT_ON_LIST,
            NewGranteeInfoOnlySuccessPageComponent,
        );
    }

    newGranteeLimited(): FlowConfig {
        return {
            flowCaption: ContentUtil.granteeFragmentsContent.acceptLimitedAuthorityLabel,
            steps: [
                //TODO add logic to conditionally choose button label text based on access type
                {
                    isStepIndicatorNeeded: false,
                    isStandaloneStep: false,
                    component: GranteePinInputComponent,
                    inputs: {},
                    primaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navContinue,
                    secondaryButton: '',
                    title: ContentUtil.granteeFragmentsContent.lblGranteeFlowProgressIndicatorSteps
                        .lblUniquePin,
                    adobeLocation: AdobeAnalyticsProcessStep.GRANTEE_NEW_PIN_LIMITED,
                    customAdobeTags: {
                        onQuit: [
                            {
                                method: AdobeMethods.TRACK_ACTION,
                                data: [
                                    {
                                        location: AdobeAnalyticsProcessStep.GRANTEE_NEW_PIN_LIMITED,
                                        ctaName: CtaNames.QUIT,
                                        ctaType: AnalyticsCtaType.BUTTON,
                                        fireTrackLink: true,
                                    },
                                ],
                            },
                        ],
                    },
                },
                {
                    isStepIndicatorNeeded: false,
                    isStandaloneStep: false,
                    component: NewGranteeRegulatoryQuestionsComponent,
                    inputs: {},
                    primaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navContinue,
                    secondaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navBack,
                    title: ContentUtil.granteeFragmentsContent.lblGranteeFlowProgressIndicatorSteps
                        .lblRegulatoryQuestions,
                    adobeLocation:
                        AdobeAnalyticsProcessStep.GRANTEE_REG_QUESTIONS_GRANTEE_NOT_ON_LIST,
                    customAdobeTags: {
                        onQuit: [
                            {
                                method: AdobeMethods.TRACK_ACTION,
                                data: [
                                    {
                                        location:
                                            AdobeAnalyticsProcessStep.GRANTEE_REG_QUESTIONS_GRANTEE_NOT_ON_LIST,
                                        ctaName: CtaNames.QUIT,
                                        ctaType: AnalyticsCtaType.BUTTON,
                                        fireTrackLink: true,
                                    },
                                ],
                            },
                        ],
                    },
                },
                {
                    isStepIndicatorNeeded: false,
                    isStandaloneStep: false,
                    component: NewGranteeAuthorizationAgreementComponent,
                    inputs: {},
                    primaryButton:
                        ContentUtil.granteeFragmentsContent.trmAuthorizationAgreement
                            .navAgreeSubmit,
                    secondaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navBack,
                    title: ContentUtil.granteeFragmentsContent.lblGranteeFlowProgressIndicatorSteps
                        .lblAuthorizationAgreement,
                    adobeLocation: AdobeAnalyticsProcessStep.GRANTEE_AUTH_AGREEMENT_NEW,
                    customAdobeTags: {
                        onQuit: [
                            {
                                method: AdobeMethods.TRACK_ACTION,
                                data: [
                                    {
                                        location:
                                            AdobeAnalyticsProcessStep.GRANTEE_AUTH_AGREEMENT_NEW,
                                        ctaName: CtaNames.QUIT,
                                        ctaType: AnalyticsCtaType.BUTTON,
                                        fireTrackLink: true,
                                    },
                                ],
                            },
                        ],
                    },
                },
                {
                    isStepIndicatorNeeded: false,
                    isStandaloneStep: true,
                    component: NewGranteeLimitedSuccessPageComponent,
                    inputs: {},
                    primaryButton: '',
                    secondaryButton: '',
                    title: 'Success page',
                    adobeLocation: null,
                },
            ],
            buttons: {
                contactUs: false,
                quit: true,
            },
        };
    }

    nonVgClientLimited(): FlowConfig {
        return {
            flowCaption: ContentUtil.granteeFragmentsContent.acceptLimitedAuthorityLabel,
            steps: [
                {
                    isStepIndicatorNeeded: false,
                    isStandaloneStep: false,
                    component: GranteePinInputComponent,
                    inputs: {},
                    primaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navContinue,
                    secondaryButton: '',
                    title: ContentUtil.granteeFragmentsContent.lblGranteeFlowProgressIndicatorSteps
                        .lblUniquePin,
                    adobeLocation: AdobeAnalyticsProcessStep.NON_VG_CLIENT_ENTER_PIN_LIMITED,
                    customAdobeTags: {
                        onQuit: [
                            {
                                method: AdobeMethods.TRACK_ACTION,
                                data: [
                                    {
                                        location:
                                            AdobeAnalyticsProcessStep.NON_VG_CLIENT_ENTER_PIN_LIMITED,
                                        ctaName: CtaNames.QUIT,
                                        ctaType: AnalyticsCtaType.BUTTON,
                                        fireTrackLink: true,
                                    },
                                ],
                            },
                        ],
                    },
                },
                {
                    isStepIndicatorNeeded: false,
                    isStandaloneStep: false,
                    component: NonVgClientGranteeRegulatoryQuestionsComponent,
                    inputs: {},
                    primaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navContinue,
                    secondaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navBack,
                    title: ContentUtil.granteeFragmentsContent.lblGranteeFlowProgressIndicatorSteps
                        .lblRegulatoryQuestions,
                    adobeLocation: AdobeAnalyticsProcessStep.NON_VG_CLIENT_QUESTIONS_LIMITED,
                    customAdobeTags: {
                        onBack: [
                            {
                                method: AdobeMethods.TRACK_ACTION,
                                data: [
                                    {
                                        location:
                                            AdobeAnalyticsProcessStep.NON_VG_CLIENT_QUESTIONS_LIMITED,
                                        ctaName: CtaNames.BACK,
                                        ctaType: AnalyticsCtaType.BUTTON,
                                        fireTrackLink: true,
                                    },
                                ],
                            },
                        ],
                        onQuit: [
                            {
                                method: AdobeMethods.TRACK_ACTION,
                                data: [
                                    {
                                        location:
                                            AdobeAnalyticsProcessStep.NON_VG_CLIENT_QUESTIONS_LIMITED,
                                        ctaName: CtaNames.QUIT,
                                        ctaType: AnalyticsCtaType.BUTTON,
                                        fireTrackLink: true,
                                    },
                                ],
                            },
                        ],
                    },
                },
                {
                    isStepIndicatorNeeded: false,
                    isStandaloneStep: false,
                    component: NonVgClientGranteeAuthorizationAgreementComponent,
                    inputs: {},
                    primaryButton:
                        ContentUtil.granteeFragmentsContent.trmAuthorizationAgreement
                            .navAgreeSubmit,
                    secondaryButton: '',
                    title: ContentUtil.granteeFragmentsContent.lblGranteeFlowProgressIndicatorSteps
                        .lblAuthorizationAgreement,
                    adobeLocation: AdobeAnalyticsProcessStep.NON_VG_CLIENT_AGREEMENT_LIMITED,
                    customAdobeTags: {
                        onBack: [
                            {
                                method: AdobeMethods.TRACK_ACTION,
                                data: [
                                    {
                                        location:
                                            AdobeAnalyticsProcessStep.NON_VG_CLIENT_AGREEMENT_LIMITED,
                                        ctaName: CtaNames.BACK,
                                        ctaType: AnalyticsCtaType.BUTTON,
                                        fireTrackLink: true,
                                    },
                                ],
                            },
                        ],
                        onQuit: [
                            {
                                method: AdobeMethods.TRACK_ACTION,
                                data: [
                                    {
                                        location:
                                            AdobeAnalyticsProcessStep.NON_VG_CLIENT_AGREEMENT_LIMITED,
                                        ctaName: CtaNames.QUIT,
                                        ctaType: AnalyticsCtaType.BUTTON,
                                        fireTrackLink: true,
                                    },
                                ],
                            },
                        ],
                    },
                },
                {
                    isStepIndicatorNeeded: false,
                    isStandaloneStep: true,
                    component: NvcLimitedSuccessPageComponent,
                    inputs: {},
                    primaryButton: '',
                    secondaryButton: '',
                    title: 'Success page',
                    adobeLocation: null,
                },
            ],
            buttons: {
                contactUs: false,
                quit: true,
            },
        };
    }

    nonVgClientInfoOnly(): FlowConfig {
        return this.createInfoOnlyFlowConfig(
            AdobeAnalyticsProcessStep.GRANTEE_NVC_SUCCESS_INFO_ONLY,
            AdobeAnalyticsProcessStep.NON_VG_CLIENT_ENTER_PIN_INFO_ONLY,
            NvcInfoOnlySuccessPageComponent,
        );
    }

    private createInfoOnlyFlowConfig(
        adobeLocation: string,
        onQuitLocation: string,
        granteeSuccessPageComponent:
            | typeof NewGranteeInfoOnlySuccessPageComponent
            | typeof NvcInfoOnlySuccessPageComponent,
    ): FlowConfig {
        return {
            flowCaption: ContentUtil.granteeFragmentsContent.lblAcceptInformationOnlyAccess,
            steps: [
                {
                    isStepIndicatorNeeded: false,
                    isStandaloneStep: false,
                    component: GranteePinInputComponent,
                    inputs: { isSubmitStep: true },
                    primaryButton: ContentUtil.grantorFragmentContent.stepperNavs.navSubmit,
                    secondaryButton: '',
                    title: ContentUtil.granteeFragmentsContent.lblGranteeFlowProgressIndicatorSteps
                        .lblUniquePin,
                    adobeLocation,
                    customAdobeTags: {
                        onQuit: [
                            {
                                method: AdobeMethods.TRACK_ACTION,
                                data: [
                                    {
                                        location: onQuitLocation,
                                        ctaName: CtaNames.QUIT,
                                        ctaType: AnalyticsCtaType.BUTTON,
                                        fireTrackLink: true,
                                    },
                                ],
                            },
                        ],
                    },
                },
                {
                    isStepIndicatorNeeded: false,
                    isStandaloneStep: true,
                    component: granteeSuccessPageComponent,
                    inputs: {},
                    primaryButton: '',
                    secondaryButton: '',
                    title: '',
                    adobeLocation: null,
                },
            ],
            buttons: {
                contactUs: false,
                quit: true,
            },
        };
    }
}
